import type {
  LEDGER_ANY,
  LedgerDateRange,
} from "@/features/clinic/constants/billing";
import type { OffsetPaginationMeta } from "@/types/pagination";
import type { User } from "@/types/user";
import type { Appointment } from "./appointment";
import type { ClinicInvoice, InvoiceSource } from "./invoice";

import { INVOICE_SOURCE } from "./invoice";

/** Receipts and advances settle the same way; one enum keeps them in step. */
export const SETTLEMENT_STATUS = {
  CLEARED: "cleared",
  PENDING: "pending",
  VOID: "void",
} as const;

export type SettlementStatus =
  (typeof SETTLEMENT_STATUS)[keyof typeof SETTLEMENT_STATUS];

/** Every ledger row comes from the same two places an invoice can. */
export const BILLING_SOURCE = INVOICE_SOURCE;

export type BillingSource = InvoiceSource;

export type ClinicReceipt = {
  id: string;
  /** Our own sequential number ("REC-000045"); `externalCode` is the CRM's. */
  code: string;
  externalId: string | null;
  /** "INV-ALC-100071" when settled from an advance, "REC-SALES-100102" direct. */
  externalCode: string | null;
  contactId: string;
  appointmentId: string | null;
  invoiceId: string | null;
  externalInvoiceId: string | null;
  externalInvoiceCode: string | null;
  source: BillingSource;
  integrationId: string | null;
  /** A string because the column is numeric(12,2); a float would round money. */
  amount: string;
  status: SettlementStatus;
  paymentType: string | null;
  settledFromAdvance: boolean;
  /** YYYY-MM-DD */
  receiptDate: string | null;
  notes: string | null;
  createdByCrmUser: string | null;
  matchedUserId: string | null;
  createdAt: string;
  updatedAt: string | null;
};

export type ClinicAdvance = {
  id: string;
  /** Our own sequential number ("ADV-000012"); `externalCode` is the CRM's. */
  code: string;
  externalId: string | null;
  /** "ADV-PT-100153" */
  externalCode: string | null;
  contactId: string;
  appointmentId: string | null;
  source: BillingSource;
  integrationId: string | null;
  amount: string;
  status: SettlementStatus;
  paymentType: string | null;
  /** YYYY-MM-DD */
  advanceDate: string | null;
  notes: string | null;
  createdByCrmUser: string | null;
  matchedUserId: string | null;
  createdAt: string;
  updatedAt: string | null;
};

/** Every figure is a numeric string ("1512.00"), never a float. */
export type ContactBillingSummary = {
  advances: string;
  fromAdvance: string;
  direct: string;
  balance: string;
  invoiced: string;
  outstanding: string;
};

export type ContactBilling = {
  summary: ContactBillingSummary;
  invoices: ClinicInvoice[];
  receipts: ClinicReceipt[];
  advances: ClinicAdvance[];
};

export type UnattachedBilling = Pick<
  ContactBilling,
  "invoices" | "receipts" | "advances"
>;

export type BillingSyncCounts = {
  invoices: number;
  receipts: number;
  advances: number;
  contactsCreated: number;
  skipped: number;
};

export const CONTACT_BILLING_SYNC_STATUS = {
  SYNCED: "synced",
  NO_CRM: "no_crm",
  /** The CRM answered; the clinic has not assigned a number to look up by. */
  NO_MRN: "no_mrn",
} as const;

export type ContactBillingSyncStatus =
  (typeof CONTACT_BILLING_SYNC_STATUS)[keyof typeof CONTACT_BILLING_SYNC_STATUS];

export type SyncContactBillingResult = {
  status: ContactBillingSyncStatus;
  mrn: string | null;
  counts: BillingSyncCounts | null;
  integrationName: string | null;
};

export type SyncContactBillingPayload = {
  contactId: string;
  integrationId?: string;
};

export type CreateAdvancePayload = {
  contactId: string;
  amount: number;
  /** YYYY-MM-DD; today when omitted. */
  advanceDate?: string;
  paymentType?: string;
  notes?: string;
};

export const APPOINTMENT_BILLING_SYNC_STATUS = {
  SYNCED: "synced",
  /** What our records hold for a CRM-linked visit; the CRM was not asked. */
  LOCAL: "local",
  NO_CRM: "no_crm",
  /** The appointment has no CRM visit id to look up by. */
  NOT_LINKED: "not_linked",
  NO_CONTACT: "no_contact",
} as const;

export type AppointmentBillingSyncStatus =
  (typeof APPOINTMENT_BILLING_SYNC_STATUS)[keyof typeof APPOINTMENT_BILLING_SYNC_STATUS];

/** The rows attached to one appointment. */
export type AppointmentLedger = Pick<
  ContactBilling,
  "invoices" | "receipts" | "advances"
>;

export type AppointmentBillingSyncResult = {
  status: AppointmentBillingSyncStatus;
  integrationName: string | null;
  /** Null unless `synced`. */
  counts: BillingSyncCounts | null;
  appointment: Appointment;
  ledger: AppointmentLedger;
  /** The contact's cleared advances less what was drawn; "0.00" with no contact. */
  advanceBalance: string;
};

export const BILLING_SYNC_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
} as const;

export type BillingSyncStatus =
  (typeof BILLING_SYNC_STATUS)[keyof typeof BILLING_SYNC_STATUS];

export const BILLING_SYNC_KIND = {
  DATE_RANGE: "date_range",
  CONTACT: "contact",
} as const;

export type BillingSyncKind =
  (typeof BILLING_SYNC_KIND)[keyof typeof BILLING_SYNC_KIND];

export type BillingSyncRun = {
  id: string;
  integrationId: string;
  kind: BillingSyncKind;
  status: BillingSyncStatus;
  requestedByUserId: string | null;
  fromDate: string | null;
  toDate: string | null;
  mrn: string | null;
  counts: BillingSyncCounts | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

export const LEDGER_TYPE = {
  INVOICE: "invoice",
  RECEIPT: "receipt",
  ADVANCE: "advance",
} as const;

export type LedgerType = (typeof LEDGER_TYPE)[keyof typeof LEDGER_TYPE];

/** Colour is for status only: money in hand reads green, money owed or a row to review amber, withdrawn red, not yet real grey. */
export type LedgerStatusTone = "success" | "warning" | "danger" | "default";

/** Enough of a workspace user to put a name on a document. */
export type BillingUser = Pick<User, "id" | "firstName" | "lastName">;

/** Who put a document on the ledger: our user when the CRM's name matched one, the CRM's spelling otherwise. */
export type LedgerCreator = {
  createdByUserId: string | null;
  createdByCrmUser: string | null;
};

/** One row of the contact ledger, flattened so a single table can show all three kinds. */
export type LedgerRow = LedgerCreator & {
  id: string;
  kind: LedgerType;
  code: string;
  externalCode: string | null;
  /** ISO timestamp or YYYY-MM-DD. */
  date: string | null;
  amount: string;
  statusLabel: string;
  tone: LedgerStatusTone;
  /** "Paid from advance", "Paid at the desk", or the invoice notes. */
  detail: string | null;
  appointmentId: string | null;
  isHeld: boolean;
};

/** A ledger row with its display strings settled, so a table only prints. */
export type LedgerTableRow = LedgerRow & {
  dateLabel: string;
  amountLabel: string;
};

/** The union of all three kinds' statuses; a value matches only its own kind. */
export const LEDGER_STATUS = {
  DRAFT: "draft",
  UNPAID: "unpaid",
  PAID: "paid",
  CLEARED: "cleared",
  PENDING: "pending",
  VOID: "void",
} as const;

export type LedgerStatus = (typeof LEDGER_STATUS)[keyof typeof LEDGER_STATUS];

/**
 * One row of the workspace-wide ledger. Flat and kind-tagged, the way the
 * server sends it: `status` is the row's own enum, so the reader labels it by
 * `kind` rather than trying to share one status union across three tables.
 */
export type BillingLedgerRow = {
  id: string;
  kind: LedgerType;
  code: string;
  externalCode: string | null;
  contactId: string;
  contactName: string | null;
  appointmentId: string | null;
  amount: string;
  /** Whichever enum this `kind` speaks - see {@link LEDGER_STATUS}. */
  status: LedgerStatus;
  /** YYYY-MM-DD */
  date: string | null;
  detail: string | null;
  source: BillingSource;
  settledFromAdvance: boolean;
  isHeld: boolean;
  createdAt: string;
};

export type BillingLedgerFilter = {
  campaignId?: string;
  page?: number;
  limit?: number;
  contactId?: string;
  types?: LedgerType[];
  statuses?: LedgerStatus[];
  source?: BillingSource;
  search?: string;
  /** YYYY-MM-DD, inclusive. */
  startDate?: string;
  endDate?: string;
  /** Receipts only: `false` is money paid at the desk, `true` a drawdown from an advance. */
  settledFromAdvance?: boolean;
  /** Receipts only: keep those settling an invoice, dated by that invoice rather than the receipt. */
  byInvoiceDate?: boolean;
  /** Invoices only: leave out the ones held as suspected duplicates, as every billed total does. */
  excludeHeld?: boolean;
  /** How it was paid, as the CRM or the desk spelled it. Case-insensitive. */
  paymentType?: string;
  /** Rows on a visit with this doctor. */
  doctorId?: string;
  /** Rows on a visit in this department, or `none`. */
  departmentId?: string;
  amountMin?: number;
  amountMax?: number;
};

/** What the filtered set adds up to. Every figure is a numeric string. */
export type BillingLedgerSummary = {
  invoiced: string;
  collected: string;
  advances: string;
};

export type BillingLedgerPage = {
  result: BillingLedgerRow[];
  meta: OffsetPaginationMeta;
  summary: BillingLedgerSummary;
  /** Every payment type on a receipt or advance in the workspace - the filter's options. */
  paymentTypes: string[];
};

/**
 * One visit's money, in the order a person asks about it: what it cost, how it
 * was paid, what is left. Every figure is a numeric string, like the summary.
 */
export type VisitBilling = {
  appointment: Appointment;
  /**
   * What the visit cost - its counting invoices, or what was collected when
   * the CRM took money without ever raising one.
   */
  billed: string;
  fromAdvance: string;
  direct: string;
  /**
   * Cleared advances banked against this visit. Credit, not revenue: the
   * from-advance receipt is what settles, so counting both would double it.
   */
  heldAsAdvance: string;
  paid: string;
  outstanding: string;
  /**
   * Whether an invoice priced this visit. False means the figure is what was
   * collected - the CRM can take money without ever raising one - so nothing
   * is outstanding and the card must not call it "billed".
   */
  isInvoiced: boolean;
  /** The first invoice note - what the CRM says was done, for a visit we hold no treatment for. */
  note: string | null;
  /** Every row hung on the visit, a free consultation's zero invoice included. */
  rows: LedgerTableRow[];
};

/** The contact ledger read as visits, plus the money none of them claims. */
export type ContactBillingVisits = {
  visits: VisitBilling[];
  /** Rows against no visit of ours - newest first. */
  unlinked: LedgerTableRow[];
  /** What has actually been taken, across everything. */
  collected: string;
  unpaidInvoiceCount: number;
  /** The oldest owed invoice's date, for saying how stale the debt is. */
  oldestUnpaidDate: string | null;
};

/** The two lines under the panel title: does this patient owe us, and what have they paid. */
export type ContactBillingPosition = {
  owes: boolean;
  headline: string;
  /** "2 unpaid invoices, oldest 12 Aug 2026" - only while something is owed. */
  headlineNote: string | null;
  detail: string;
};

export interface ToBillingVisitsInput {
  billing: ContactBilling;
  appointments: Appointment[];
}

/** A record line in plain words: what happened, its status, then when and under which number. */
export type LedgerLineCopy = {
  primary: string;
  statusLabel: string;
  tone: LedgerStatusTone;
  secondary: string;
};

/** A visit row's strings, settled so the row only prints. */
export type VisitBillingDisplay = {
  title: string;
  subtitle: string;
  /** Null when nothing has been charged and nothing taken. */
  billedLabel: string | null;
  /** Null once the visit is square. */
  outstandingLabel: string | null;
  statusLabel: string;
  /**
   * Colour is for the states somebody has to do something about. A settled
   * visit reads neutral on purpose - a wall of green would drown the two
   * rows that actually owe money.
   */
  statusTone: VisitStatusTone;
};

export type VisitStatusTone = "default" | "success" | "warning" | "danger";

/** A ledger row resolved back to the record behind it. */
export type LedgerRecord =
  | { kind: "invoice"; invoice: ClinicInvoice }
  | { kind: "receipt"; receipt: ClinicReceipt }
  | { kind: "advance"; advance: ClinicAdvance };

export type LedgerRecordField = {
  label: string;
  value: string;
  /** Money lines up under the total on the right; facts read from the left. */
  align?: "start" | "end";
  /** Only for a figure somebody has to act on, like what is still outstanding. */
  tone?: LedgerStatusTone;
};

/**
 * One document or one visit, laid out for reading: a title, the header
 * figures, its facts, then the documents tied to it.
 */
export type LedgerDetail = {
  title: string;
  subtitle: string | null;
  statusLabel: string;
  tone: LedgerStatusTone;
  amountLabel: string;
  /** What this document means, in one line - only when there is something to say. */
  summary: string | null;
  fields: LedgerRecordField[];
  relatedLabel: string | null;
  related: LedgerTableRow[];
};

export interface ToLedgerRecordDetailInput {
  record: LedgerRecord;
  billing: ContactBilling;
  appointments: Appointment[];
  crmName: string | null;
  users: BillingUser[];
}

/** What a link into the Documents ledger can pre-set - a report figure lands on the rows behind it. */
export interface LedgerUrlSeed {
  documents: boolean;
  type: LedgerType | typeof LEDGER_ANY;
  statuses: LedgerStatus[];
  dateRange: LedgerDateRange;
  excludeHeld?: boolean;
}
